import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { tenantApi } from '../../api/endpoints/tenant';
import { useAuth } from '../../hooks/useAuth';
import { SELECT_CLASS } from '../../lib/selectClass';
import {
    allowedScopesForRole,
    canActorAssignScope,
    groupRolesByScope,
    strongestActorScope,
} from '../../lib/roleScopes';
import { organizationName } from '../../lib/scopeLabels';
import { PLATFORM_SCOPE_NAME } from '../../lib/labelConstants';
import { LevelEntityPicker, isGenericLevel } from '../scoped/LevelEntityPicker';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import type { LabelResolver } from '../../hooks/useLabelResolver';
import type { Scope } from '../../api/types/permissions';
import { API_ROLE_DISABLED_NOTE, isRoleForbiddenForApiUser } from '../../lib/apiUserRoles';

/**
 * Selección rol → scope → scopeRef, EXTRAÍDA de `RoleAssignmentDialog` para reusarla en
 * `ManageUserRolesDialog`. Es un campo controlado: maneja su propio estado interno (rol elegido,
 * retailer encadenado y refs marcados) y notifica al padre vía `onChange` el resultado normalizado
 * a asignar: `{ roleId, roleScope, refsToAssign }` (o `null` mientras falte rol/destino).
 *
 *  - TENANT      → refsToAssign = [tenantId] (se muestra el nombre de la organización)
 *  - PLATFORM    → refsToAssign = ['*'] (se muestra «Plataforma»)
 *  - LEVEL_1/2/3 → picker de entidades de nivel (`<LevelEntityPicker>`): L1 multi-select directo;
 *                  L2/L3 eligen la cadena de padres y luego multi-select de hojas (refsToAssign = ids).
 *
 * Los roles del dropdown van AGRUPADOS por nivel (`groupRolesByScope`) y filtrados a los que el
 * actor puede asignar (`canActorAssignScope`, espeja el rank del backend). El backend re-enforza.
 */

export interface RoleScopeSelection {
    roleId: string;
    roleScope: Scope;
    refsToAssign: string[];
}

interface Props {
    /** Tenant donde se asigna (drill-down platform o el del actor en retailer). */
    tenantId: string;
    /** Solo dispara queries cuando el contenedor (dialog) está abierto. */
    open: boolean;
    /** Notifica la selección normalizada lista para asignar, o `null` si está incompleta. */
    onChange: (selection: RoleScopeSelection | null) => void;
    /** Prefijo para los `id`/`htmlFor` (evita colisiones si hay 2 instancias montadas). */
    idPrefix?: string;
    /** Resolvedor del tenant donde se asigna (el visto en platform, el propio en tenant). */
    labels: LabelResolver;
    /** El usuario es un sistema (API): los roles de administración quedan deshabilitados. */
    forApiUser?: boolean;
}

export function RoleScopeFields({
    tenantId,
    open,
    onChange,
    idPrefix = 'rsf',
    labels,
    forApiUser = false,
}: Props) {
    const { roleAssignments, hasPermission } = useAuth();
    // Scope del actor + create.lateral → solo se ofrecen roles que el actor puede asignar (espeja backend).
    const actorScope = strongestActorScope(roleAssignments);
    const canLateral = hasPermission('tenant.user.create.lateral');

    const [roleId, setRoleId] = useState<string | null>(null);
    // scopeRefs marcados en el multi-select (LEVEL_1 → entityIds).
    const [selectedRefs, setSelectedRefs] = useState<string[]>([]);

    const {
        data: roles,
        isError,
        refetch,
    } = useQuery({
        queryKey: ['tenant', tenantId, 'roles'],
        queryFn: () => tenantApi.listRoles(tenantId),
        enabled: open,
    });

    // Solo roles cuyo scope el actor puede asignar (usa el scope REAL del rol → vale para custom).
    const assignableRoles = useMemo(
        () =>
            (roles?.items ?? []).filter((r) =>
                canActorAssignScope(actorScope, r.scope, canLateral),
            ),
        [roles, actorScope, canLateral],
    );
    const apiForbiddenRoleIds = useMemo(
        () =>
            new Set(
                forApiUser
                    ? assignableRoles.filter(isRoleForbiddenForApiUser).map((r) => r.roleId)
                    : [],
            ),
        [assignableRoles, forApiUser],
    );
    const groupedRoles = useMemo(
        () => groupRolesByScope(assignableRoles, labels, tenantId),
        [assignableRoles, labels, tenantId],
    );

    // Nivel del destino = scope REAL del rol elegido. Fallback a la matriz seed solo si el list no
    // lo trae (backend viejo / rol custom sin scope).
    const roleScope = useMemo<Scope | null>(() => {
        if (!roleId) return null;
        const item = roles?.items.find((r) => r.roleId === roleId);
        return item?.scope ?? allowedScopesForRole(roleId)[0];
    }, [roleId, roles]);

    // Notifica al padre la selección normalizada (o null si incompleta). Se llama en cada handler de
    // cambio en vez de en un effect (el lint del repo prohíbe setState/efectos derivados aquí).
    // scopeRefs efectivos: TENANT → [tenantId]; PLATFORM → ['*']; LEVEL_1 → los marcados.
    const notify = (nextRoleId: string | null, nextRefs: string[]): void => {
        if (!nextRoleId) {
            onChange(null);
            return;
        }
        const item = roles?.items.find((r) => r.roleId === nextRoleId);
        // `allowedScopesForRole` siempre devuelve al menos un scope (fallback CUSTOM) → no es undefined.
        const scope: Scope = item?.scope ?? allowedScopesForRole(nextRoleId)[0];
        const fixed = scope === 'TENANT' ? tenantId : scope === 'PLATFORM' ? '*' : null;
        const refs = fixed !== null ? [fixed] : nextRefs;
        onChange(
            refs.length > 0 ? { roleId: nextRoleId, roleScope: scope, refsToAssign: refs } : null,
        );
    };

    const handleRoleChange = (id: string): void => {
        setRoleId(id);
        setSelectedRefs([]);
        notify(id, []);
    };
    // El picker de nivel entrega el array completo de hojas marcadas; se refleja y se notifica.
    const handleLevelChange = (refs: string[]): void => {
        setSelectedRefs(refs);
        notify(roleId, refs);
    };

    if (isError) {
        return (
            <div className="space-y-2 rounded-md border border-destructive/40 p-3">
                <p className="text-sm text-destructive">No pudimos cargar los roles.</p>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                        void refetch();
                    }}
                >
                    Reintentar
                </Button>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="space-y-1">
                <select
                    id={`${idPrefix}-role`}
                    aria-label="Rol"
                    className={SELECT_CLASS}
                    value={roleId ?? ''}
                    onChange={(e) => {
                        handleRoleChange(e.target.value);
                    }}
                >
                    <option value="" disabled>
                        Elige un rol
                    </option>
                    {groupedRoles.map((g) => (
                        <optgroup key={g.key} label={g.label}>
                            {g.roles.map((r) => {
                                const disabled = apiForbiddenRoleIds.has(r.roleId);
                                return (
                                    <option key={r.roleId} value={r.roleId} disabled={disabled}>
                                        {disabled
                                            ? `${r.roleName} (${API_ROLE_DISABLED_NOTE})`
                                            : r.roleName}
                                    </option>
                                );
                            })}
                        </optgroup>
                    ))}
                </select>
            </div>

            {roleScope === 'TENANT' && (
                <div className="space-y-1">
                    <Label htmlFor={`${idPrefix}-dest-tenant`}>Destino</Label>
                    <input
                        id={`${idPrefix}-dest-tenant`}
                        className={SELECT_CLASS}
                        value={organizationName(labels, tenantId)}
                        readOnly
                        disabled
                    />
                </div>
            )}

            {roleScope === 'PLATFORM' && (
                <div className="space-y-1">
                    <Label htmlFor={`${idPrefix}-dest-platform`}>Destino</Label>
                    <input
                        id={`${idPrefix}-dest-platform`}
                        className={SELECT_CLASS}
                        value={PLATFORM_SCOPE_NAME}
                        readOnly
                        disabled
                    />
                </div>
            )}

            {isGenericLevel(roleScope) && (
                // DEC-RBAC-090 / DEC-027: destino de un rol de nivel genérico (L1/L2/L3). `key={roleScope}`
                // remonta el picker al cambiar de nivel → reinicia su cadena de padres interna.
                <LevelEntityPicker
                    key={roleScope}
                    tenantId={tenantId}
                    level={roleScope}
                    value={selectedRefs}
                    onChange={handleLevelChange}
                    idPrefix={idPrefix}
                    open={open}
                    labels={labels}
                />
            )}
        </div>
    );
}
